import {
  AfterViewInit,
  Directive,
  ElementRef,
  EventEmitter,
  HostListener,
  Input,
  OnDestroy,
  Output,
} from '@angular/core';
import intlTelInput from 'intl-tel-input';
import utils from 'intl-tel-input/utils';

export type IntlTelInputOptions = Parameters<typeof intlTelInput>[1];
export type IntlTelInputInstance = ReturnType<typeof intlTelInput>;

@Directive({
  selector: '[appIntlTelInput]',
  standalone: true,
})
export class IntlTelInputDirective implements AfterViewInit, OnDestroy {

  @Input() itiOptions: IntlTelInputOptions = {};
  @Output() internationalNumberChange = new EventEmitter<string>();

  private iti?: IntlTelInputInstance;

  constructor(private elementRef: ElementRef<HTMLInputElement>) { }

  ngAfterViewInit(): void {
    const defaultOptions: IntlTelInputOptions = {
      initialCountry: 'us',
      separateDialCode: true,
      loadUtils: () => import('intl-tel-input/utils'),
    };

    this.iti = intlTelInput(this.elementRef.nativeElement, {
      ...defaultOptions,
      ...this.itiOptions,
    });
  }

  ngOnDestroy(): void {
    this.iti?.destroy();
  }

  @HostListener('blur')
  syncInternationalNumber(): void {
    const number = this.getInternationalNumber();
    if (number) this.internationalNumberChange.emit(number);
  }

  /** Returns intl-tel-input instance */
  getInstance(): IntlTelInputInstance | undefined {
    return this.iti;
  }

  /**
   * Returns the selected country's standard international format, e.g.
   * "+1 777-777-7777" for a US number. This is the value sent to APIs.
   */
  getInternationalNumber(): string {
    const number = this.iti?.getNumber(utils.numberFormat.INTERNATIONAL);
    if (number) return number;

    const rawNumber = this.elementRef.nativeElement.value.trim();
    const dialCode = this.iti?.getSelectedCountryData()?.dialCode;

    if (!rawNumber || !dialCode) return rawNumber;

    const prefix = `+${dialCode}`;
    if (rawNumber.startsWith(prefix)) {
      return rawNumber;
    }

    return rawNumber.startsWith('+') ? rawNumber : `${prefix} ${rawNumber}`;
  }

  /** Returns selected country data */
  getSelectedCountry() {
    return this.iti?.getSelectedCountryData();
  }

  /** Validates phone number */
  isValidNumber(): boolean {
    return this.iti?.isValidNumber() ?? false;
  }
}
